import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ContextSession } from "../../../services/ContextSession";

const API_URL =
  "https://be.injupemp.gob.hn:8443/api/reportes/asignaciones-por-mes/";
const MONTHS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    backgroundColor: "#19438C",
    color: "white",
    fontSize: 15,
    textAlign: "center",
  },
  "&.MuiTableCell-body": {
    fontSize: 14,
    textAlign: "center",
  },
  "&.MuiTableCell-body:nth-of-type(2)": {
    textAlign: "left",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatPercentage(value, total) {
  const numerator = toNumber(value);
  const denominator = toNumber(total);
  if (denominator <= 0) return "0%";
  const percentage = (numerator / denominator) * 100;
  const rounded =
    Number.isInteger(percentage) ? percentage.toFixed(0) : percentage.toFixed(1);
  return `${rounded}%`;
}

function formatCountWithPercentage(value, total) {
  return `${toNumber(value)} (${formatPercentage(value, total)})`;
}

function normalizeRows(payload) {
  if (!Array.isArray(payload)) return [];
  return payload.map((item, index) => ({
    id: toNumber(item.UsuarioID, index + 1),
    username: item.username || "",
    nombreCompleto:
      item.NombreCompleto ||
      item.nombreCompleto ||
      item.nombre_completo ||
      "",
    anio: toNumber(item.Anio ?? item.Año),
    mes: toNumber(item.Mes),
    totalAsignadas: toNumber(item.Total_Asignadas),
    totalContestadas: toNumber(item.Total_Contestadas),
    totalFaltantes: toNumber(item.Total_Faltantes),
  }));
}

async function fetchReporte(token, mes, anio) {
  const authHeader =
    token && token !== "NA"
      ? {
          Authorization: token.startsWith("Bearer ")
            ? token
            : `Bearer ${token}`,
        }
      : {};

  const requests = [
    `${API_URL}?mes=${mes}&anio=${anio}`,
    `${API_URL}?Mes=${mes}&Anio=${anio}`,
    API_URL,
  ];

  let lastError = null;
  for (const url of requests) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
      });

      if (!response.ok) {
        lastError = new Error(`HTTP ${response.status}`);
        continue;
      }

      const json = await response.json();
      return normalizeRows(json);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("No se pudo obtener el reporte.");
}

function ReportePreanalisis() {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const sessi = useContext(ContextSession);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const [mes, setMes] = useState(currentDate.getMonth() + 1);
  const [anio, setAnio] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const yearOptions = useMemo(() => {
    const baseYears = [
      currentYear - 2,
      currentYear - 1,
      currentYear,
      currentYear + 1,
    ];
    return [...new Set(baseYears)].sort((a, b) => b - a);
  }, [currentYear]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const hasData =
        row.totalAsignadas > 0 ||
        row.totalContestadas > 0 ||
        row.totalFaltantes > 0;
      return row.mes === mes && row.anio === anio && hasData;
    });
  }, [rows, mes, anio]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.asignadas += toNumber(row.totalAsignadas);
        acc.contestadas += toNumber(row.totalContestadas);
        acc.faltantes += toNumber(row.totalFaltantes);
        return acc;
      },
      { asignadas: 0, contestadas: 0, faltantes: 0 }
    );
  }, [filteredRows]);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError("");

      try {
        const data = await fetchReporte(sessi.sessionData.token, mes, anio);
        if (!cancelled) {
          setRows(data);
          setPage(0);
        }
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setError("No se pudo cargar el reporte de preanálisis.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, [sessi.sessionData.token, mes, anio]);

  const pagedRows =
    rowsPerPage > 0
      ? filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : filteredRows;

  return (
    <div className="container" style={{ marginTop: 10 }}>
      <Box sx={{ mb: 2, textAlign: "center" }}>
        <Typography variant="h5" fontWeight={700}>
          Reporte de Preanálisis por Usuario
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="mes-label">Mes</InputLabel>
            <Select
              labelId="mes-label"
              value={mes}
              label="Mes"
              onChange={(event) => setMes(Number(event.target.value))}
            >
              {MONTHS.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="anio-label">Año</InputLabel>
            <Select
              labelId="anio-label"
              value={anio}
              label="Año"
              onChange={(event) => setAnio(Number(event.target.value))}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            minHeight: 180,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Usuario</StyledTableCell>
                  <StyledTableCell>NombreCompleto</StyledTableCell>
                  <StyledTableCell>Asignadas</StyledTableCell>
                  <StyledTableCell>Contestadas</StyledTableCell>
                  <StyledTableCell>Faltantes</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.length > 0 ? (
                  <>
                    {pagedRows.map((row, index) => (
                      <StyledTableRow key={`${row.id}-${row.username}-${index}`}>
                        <StyledTableCell>{row.username}</StyledTableCell>
                        <StyledTableCell>{row.nombreCompleto || "-"}</StyledTableCell>
                        <StyledTableCell>{row.totalAsignadas}</StyledTableCell>
                        <StyledTableCell>
                          {formatCountWithPercentage(
                            row.totalContestadas,
                            row.totalAsignadas
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          {formatCountWithPercentage(
                            row.totalFaltantes,
                            row.totalAsignadas
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                    <TableRow
                      sx={{
                        backgroundColor: "#E8EEF9",
                        "& td": { fontWeight: 700 },
                      }}
                    >
                      <TableCell colSpan={2} sx={{ textAlign: "right" }}>
                        Totales
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        {totals.asignadas}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        {formatCountWithPercentage(
                          totals.contestadas,
                          totals.asignadas
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        {formatCountWithPercentage(
                          totals.faltantes,
                          totals.asignadas
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={5}>
                      No hay información para el mes y año seleccionados.
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredRows.length > 0 && (
            <TablePagination
              component="div"
              rowsPerPageOptions={isMobile ? [5, 10] : [10, 20, 50]}
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={isMobile ? "Filas:" : "Filas por página"}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          )}
        </Paper>
      )}
    </div>
  );
}

export default ReportePreanalisis;
